/*
 * Abba & Co. — theme behavior (docs/design-handoff-round1).
 * Loaded once from layout/theme.liquid with defer. Custom elements only; each
 * section works without JavaScript and is enhanced here.
 */

/* ---------------------------------------------------------------------------
 * <abba-header>: sections/abbaco-header.liquid
 * - Overlay mode (homepage): transparent until scrollY > 24 or the season
 *   panel opens, then solid (.is-solid).
 * - By season panel: opens on hover or click of the toggle; closes on
 *   Escape, click outside, or the mouse leaving the header.
 * - Mobile menu: dialog with focus trap, Escape to close, focus returned to
 *   the trigger, body scroll locked, 220ms fade.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-header')) {
  customElements.define(
    'abba-header',
    class AbbaHeader extends HTMLElement {
      connectedCallback() {
        this.overlay = this.dataset.overlay === 'true';
        this.bar = this.querySelector('.ab-header__bar');
        this.seasonToggle = this.querySelector('[data-season-toggle]');
        this.panel = this.querySelector('#AbSeasonPanel');
        this.menu = this.querySelector('#AbMobileMenu');
        this.menuOpenButton = this.querySelector('[data-menu-open]');
        this.desktop = window.matchMedia('(min-width: 1024px)');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.onScroll = this.updateSolid.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);

        if (this.overlay) {
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.updateSolid();
        }
        document.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onDocumentClick);

        if (this.seasonToggle && this.panel) {
          this.seasonToggle.addEventListener('click', (event) => {
            event.stopPropagation();
            this.setPanel(!this.panelOpen);
          });
          this.seasonToggle.addEventListener('mouseenter', () => {
            if (this.desktop.matches) this.setPanel(true);
          });
          this.bar.addEventListener('mouseleave', () => this.setPanel(false));
        }

        this.setupSearch();

        if (this.menu && this.menuOpenButton) {
          this.menuOpenButton.addEventListener('click', () => this.openMenu());
          this.menu.querySelector('[data-menu-close]')?.addEventListener('click', () => this.closeMenu());
          this.menu.addEventListener('keydown', (event) => this.trapMenuFocus(event));

          const seasonsToggle = this.menu.querySelector('[data-menu-seasons]');
          const seasonsList = this.menu.querySelector('#AbMenuSeasons');
          seasonsToggle?.addEventListener('click', () => {
            const open = seasonsToggle.getAttribute('aria-expanded') !== 'true';
            seasonsToggle.setAttribute('aria-expanded', String(open));
            if (seasonsList) seasonsList.hidden = !open;
          });

          // Bag from inside the menu: close the menu, then open the drawer.
          this.menu.querySelector('[data-menu-bag]')?.addEventListener('click', (event) => {
            const drawer = document.querySelector('cart-drawer');
            if (!drawer) return;
            event.preventDefault();
            this.closeMenu(false);
            setTimeout(() => drawer.open(this.querySelector('#cart-icon-bubble')), 240);
          });
        }
      }

      /* Predictive search panel: opens from the header Search link (which
         still navigates to /search without JavaScript). */
      setupSearch() {
        this.searchWrap = this.querySelector('#AbSearchWrap');
        this.searchPanel = this.querySelector('#AbSearch');
        this.searchInput = this.querySelector('[data-search-input]');
        if (!this.searchWrap || !this.searchInput) return;
        this.searchDefault = this.querySelector('[data-search-default]');
        this.searchLive = this.querySelector('[data-search-live]');
        this.searchCount = this.querySelector('[data-search-count]');
        this.searchTriggers = this.querySelectorAll('[data-search-open]');

        this.searchTriggers.forEach((trigger) => {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.openSearch();
          });
        });
        this.searchWrap.querySelectorAll('[data-search-close]').forEach((el) =>
          el.addEventListener('click', () => this.closeSearch())
        );
        this.searchPanel.addEventListener('keydown', (event) => this.trapFocus(event, this.searchPanel, () => this.closeSearch()));
        this.searchInput.addEventListener(
          'input',
          debounce(() => this.runSearch(), 250)
        );
      }

      openSearch() {
        this.setPanel(false);
        this.lastFocus = document.activeElement;
        this.searchWrap.hidden = false;
        this.searchTriggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
        document.body.style.overflow = 'hidden';
        void this.searchWrap.offsetHeight;
        this.searchWrap.classList.add('is-shown');
        this.searchInput.focus();
      }

      closeSearch() {
        this.searchWrap.classList.remove('is-shown');
        this.searchTriggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
        document.body.style.overflow = '';
        const finish = () => {
          this.searchWrap.hidden = true;
          if (this.lastFocus?.focus) this.lastFocus.focus();
        };
        if (this.reduceMotion.matches) finish();
        else setTimeout(finish, 220);
      }

      runSearch() {
        const q = this.searchInput.value.trim();
        this.searchController?.abort();
        if (!q) {
          this.searchLive.innerHTML = '';
          this.searchDefault.hidden = false;
          this.searchCount.textContent = 'Start typing, or begin with a season.';
          return;
        }
        this.searchController = new AbortController();
        const params = new URLSearchParams({
          q,
          section_id: 'abbaco-predictive-search',
          'resources[type]': 'product,collection,article,page',
          'resources[limit]': '6',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last',
        });
        fetch(`${window.routes.predictive_search_url}?${params}`, { signal: this.searchController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const results = doc.querySelector('[data-predictive-results]');
            if (!results) return;
            const count = results.querySelector('[data-predictive-count]');
            this.searchCount.textContent = count && results.querySelector('.ab-search-panel__grid') ? count.textContent.trim() : '';
            count?.remove();
            this.searchDefault.hidden = true;
            this.searchLive.innerHTML = results.innerHTML;
          })
          .catch((error) => {
            if (error.name !== 'AbortError') this.searchCount.textContent = '';
          });
      }

      trapFocus(event, container, onEscape) {
        if (event.key === 'Escape') {
          event.preventDefault();
          onEscape();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [
          ...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"])'),
        ].filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onDocumentClick);
      }

      get panelOpen() {
        return this.classList.contains('is-panel-open');
      }

      updateSolid() {
        if (!this.overlay) return;
        this.classList.toggle('is-solid', window.scrollY > 24 || this.panelOpen);
      }

      setPanel(open) {
        if (!this.seasonToggle || open === this.panelOpen) return;
        this.classList.toggle('is-panel-open', open);
        this.seasonToggle.setAttribute('aria-expanded', String(open));
        this.updateSolid();
      }

      handleKeyDown(event) {
        if (event.key !== 'Escape') return;
        if (this.panelOpen) {
          const focusWasInPanel = this.panel?.contains(document.activeElement);
          this.setPanel(false);
          if (focusWasInPanel) this.seasonToggle.focus();
        }
      }

      handleDocumentClick(event) {
        if (this.panelOpen && !this.bar.contains(event.target)) this.setPanel(false);
      }

      openMenu() {
        this.lastFocus = document.activeElement;
        this.menu.hidden = false;
        this.menuOpenButton.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        void this.menu.offsetHeight; // commit display before the fade starts
        this.menu.classList.add('is-shown');
        this.menu.querySelector('[data-menu-close]')?.focus();
      }

      closeMenu(returnFocus = true) {
        this.menu.classList.remove('is-shown');
        this.menuOpenButton.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        const finish = () => {
          this.menu.hidden = true;
          if (returnFocus && this.lastFocus?.focus) this.lastFocus.focus();
        };
        if (this.reduceMotion.matches) finish();
        else setTimeout(finish, 220);
      }

      trapMenuFocus(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeMenu();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [
          ...this.menu.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        ].filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-signup>: sections/abbaco-newsletter.liquid
 * Client-side email check before Shopify's customer form posts. An invalid
 * entry sets aria-invalid (Blaze border via CSS) and writes the message into
 * the form's role="status" region; the server response still wins on reload.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-signup')) {
  customElements.define(
    'abba-signup',
    class AbbaSignup extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.input = this.querySelector('input[type="email"]');
        this.status = this.form?.querySelector('[role="status"]');
        if (!this.form || !this.input) return;
        this.form.addEventListener('submit', (event) => {
          const value = this.input.value.trim();
          const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
          this.input.setAttribute('aria-invalid', String(!valid));
          if (!valid) {
            event.preventDefault();
            if (this.status) this.status.textContent = 'Enter an email address, for example name@example.com.';
            this.input.focus();
          }
        });
        this.input.addEventListener('input', () => {
          if (this.input.getAttribute('aria-invalid') === 'true') this.input.setAttribute('aria-invalid', 'false');
        });
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-product>: sections/abbaco-main-product.liquid
 * Dawn's <product-form> handles the add to cart and the drawer. This adds:
 * swatch/option selection -> variant id, price, legend, availability, URL;
 * the quantity stepper (minus disabled at 1); the "Adding to bag" busy
 * label with aria-busy; and the mobile gallery "n / total" counter.
 * Without JavaScript the variant <select> stays visible and submits.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-product')) {
  customElements.define(
    'abba-product',
    class AbbaProduct extends HTMLElement {
      connectedCallback() {
        try {
          this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
        } catch (e) {
          this.variants = [];
        }
        this.select = this.querySelector('[data-variant-select]');
        this.price = this.querySelector('[data-price]');
        this.addButton = this.querySelector('[data-add-button]');
        this.addLabel = this.querySelector('[data-add-label]');

        if (this.select) {
          this.select.hidden = true;
          this.querySelectorAll('[data-option-fieldset]').forEach((fieldset) => (fieldset.hidden = false));
          this.querySelectorAll('[data-option-position]').forEach((input) =>
            input.addEventListener('change', () => this.onOptionChange())
          );
        }

        this.setupStepper();
        this.setupBusyLabel();
        this.setupGallery();
        this.setupNotify();
        this.setupSticky();
      }

      /* Sticky add-to-bag bar: shown once the main buy row has scrolled
         above the viewport, hidden within 700px of the page end and while
         the selected variant is sold out. Its button submits the form. */
      setupSticky() {
        this.sticky = this.querySelector('[data-sticky-buy]');
        const buy = this.querySelector('[data-buy]');
        if (!this.sticky || !buy) return;
        const button = this.sticky.querySelector('[data-sticky-add]');
        button?.addEventListener('click', () => this.addButton?.click());
        const swatch = this.querySelector('[data-option-position]:checked + .ab-swatch__chip');
        if (swatch) this.sticky.querySelector('[data-sticky-swatch]').style.cssText = swatch.getAttribute('style') || '';
        const update = () => {
          const past = buy.getBoundingClientRect().bottom < 0;
          const nearEnd = window.innerHeight + window.scrollY > document.documentElement.scrollHeight - 700;
          const show = past && !nearEnd && !buy.hidden && !this.addButton?.disabled;
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', String(!show));
          if (button) button.tabIndex = show ? 0 : -1;
        };
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
      }

      syncSticky(variant) {
        if (!this.sticky || !variant) return;
        const option = this.sticky.querySelector('[data-sticky-option]');
        const price = this.sticky.querySelector('[data-sticky-price]');
        if (option) option.textContent = variant.option1 || '';
        if (price) price.textContent = this.formatMoney(variant.price);
        const swatch = this.querySelector('[data-option-position]:checked + .ab-swatch__chip');
        if (swatch) this.sticky.querySelector('[data-sticky-swatch]').style.cssText = swatch.getAttribute('style') || '';
        const label = this.sticky.querySelector('[data-sticky-add]');
        if (label) label.textContent = variant.available ? this.dataset.addIdle || 'Add to bag' : 'Sold out';
      }

      selectedOptions() {
        const byPosition = {};
        this.querySelectorAll('[data-option-position]:checked').forEach((input) => {
          byPosition[input.dataset.optionPosition] = input.value;
        });
        return Object.keys(byPosition)
          .sort()
          .map((position) => byPosition[position]);
      }

      onOptionChange() {
        const options = this.selectedOptions();
        const variant = this.variants.find((v) => options.every((value, i) => v.options[i] === value));

        this.querySelectorAll('[data-option-label]').forEach((label) => {
          label.textContent = `· ${options[Number(label.dataset.optionLabel) - 1] || ''}`;
        });

        if (!variant) {
          this.setAvailability(false, 'Unavailable');
          return;
        }
        this.select.value = String(variant.id);
        if (this.price) this.price.textContent = this.formatMoney(variant.price);
        this.setAvailability(variant.available, variant.available ? this.dataset.addIdle || 'Add to bag' : 'Sold out', variant);
        this.syncSticky(variant);

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      setAvailability(available, label, variant) {
        if (this.addButton) {
          this.addButton.disabled = !available;
          if (this.addLabel) this.addLabel.textContent = label;
        }
        // Sold out: tag by the price, Sand button + notify form instead of buy.
        const soldOut = !available && label === 'Sold out';
        this.querySelector('[data-soldout-tag]')?.toggleAttribute('hidden', !soldOut);
        this.querySelector('[data-buy]')?.toggleAttribute('hidden', soldOut);
        this.querySelector('[data-soldout]')?.toggleAttribute('hidden', !soldOut);
        if (variant) {
          const color = this.querySelector('[data-notify-color]');
          if (color) color.textContent = variant.option1 || variant.title;
          const body = this.querySelector('[data-notify-body]');
          if (body) body.value = `Notify me when ${this.dataset.productTitle} (${variant.title}) is back.`;
        }
      }

      formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || 'USD';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency,
          }).format(cents / 100);
        } catch (e) {
          return (cents / 100).toFixed(2);
        }
      }

      setupStepper() {
        const stepper = this.querySelector('[data-stepper]');
        if (!stepper) return;
        const input = stepper.querySelector('input');
        const minus = stepper.querySelector('[data-step="-1"]');
        const sync = () => {
          const value = Math.max(1, parseInt(input.value, 10) || 1);
          input.value = value;
          minus.disabled = value <= 1;
        };
        stepper.querySelectorAll('[data-step]').forEach((button) =>
          button.addEventListener('click', () => {
            input.value = (parseInt(input.value, 10) || 1) + Number(button.dataset.step);
            sync();
          })
        );
        input.addEventListener('change', sync);
        sync();
      }

      setupBusyLabel() {
        const form = this.querySelector('form');
        if (!form || !this.addButton || !this.addLabel) return;
        form.addEventListener('submit', () => {
          if (this.addButton.disabled) return;
          this.idleLabel = this.addLabel.textContent;
          this.addLabel.textContent = this.dataset.addBusy || 'Adding to bag';
          this.addButton.setAttribute('aria-busy', 'true');
        });
        // Dawn toggles .loading on the button while the request runs.
        new MutationObserver(() => {
          if (this.addButton.getAttribute('aria-busy') === 'true' && !this.addButton.classList.contains('loading')) {
            this.addButton.setAttribute('aria-busy', 'false');
            if (this.idleLabel) this.addLabel.textContent = this.idleLabel;
          }
        }).observe(this.addButton, { attributes: true, attributeFilter: ['class'] });
      }

      /* Notify me: posts Shopify's contact form in the background and shows
         "Thank you. We will write once, when {color} is back." */
      setupNotify() {
        const form = this.querySelector('[data-notify-form]');
        if (!form) return;
        const input = form.querySelector('input[type="email"]');
        const msg = form.querySelector('[data-notify-msg]');
        const fields = form.querySelector('[data-notify-fields]');
        if (!input || !msg) return;
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.value.trim());
          input.setAttribute('aria-invalid', String(!valid));
          if (!valid) {
            msg.textContent = 'Enter an email address, for example name@example.com.';
            input.focus();
            return;
          }
          const color = form.querySelector('[data-notify-color]')?.textContent.trim() || 'it';
          fetch(form.action, { method: 'POST', body: new FormData(form) })
            .then((response) => {
              if (!response.ok) throw new Error(response.status);
              if (fields) fields.hidden = true;
              msg.textContent = `Thank you. We will write once, when ${color} is back.`;
            })
            .catch(() => form.submit());
        });
      }

      setupGallery() {
        const gallery = this.querySelector('[data-gallery]');
        const pos = this.querySelector('[data-gallery-pos]');
        if (!gallery || !pos) return;
        gallery.addEventListener(
          'scroll',
          () => {
            const item = gallery.firstElementChild;
            if (!item) return;
            const step = item.getBoundingClientRect().width + 8;
            pos.textContent = String(Math.min(gallery.children.length, Math.round(gallery.scrollLeft / step) + 1));
          },
          { passive: true }
        );
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-reveal>: sections/abbaco-verse-reveal.liquid
 * The region is visible without JavaScript. Here it starts closed; the
 * button toggles aria-expanded and the region fades in and rises 8px over
 * 260ms (opacity only under reduced motion). Enter/Space work natively.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-reveal')) {
  customElements.define(
    'abba-reveal',
    class AbbaReveal extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-reveal-toggle]');
        this.region = this.querySelector('[data-reveal-region]');
        if (!this.button || !this.region) return;
        this.setOpen(false, true);
        this.button.addEventListener('click', () => this.setOpen(this.button.getAttribute('aria-expanded') !== 'true'));
      }

      setOpen(open, instant = false) {
        this.button.setAttribute('aria-expanded', String(open));
        this.classList.toggle('is-open', open);
        clearTimeout(this.timer);
        if (open) {
          this.region.hidden = false;
          void this.region.offsetHeight; // commit the closed state so the transition runs
          this.region.classList.add('is-open');
        } else {
          this.region.classList.remove('is-open');
          if (instant) this.region.hidden = true;
          else this.timer = setTimeout(() => (this.region.hidden = true), 260);
        }
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-gift-note>: snippets/abbaco-gift-note.liquid (drawer + cart page)
 * Saves the "Gift note" cart attribute through /cart/update.js as the
 * shopper types (debounced), then announces "Your note is saved with your
 * bag." in the role="status" region. Mirrors the text into the live
 * preview card; an empty note shows "Your words will appear here." in Muted.
 * Checkout still submits the textarea with the cart form without JS.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-gift-note')) {
  customElements.define(
    'abba-gift-note',
    class AbbaGiftNote extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-gift-input]');
        this.status = this.querySelector('[data-gift-status]');
        this.preview = this.querySelector('[data-gift-preview]');
        if (!this.input) return;
        this.save = debounce(this.save.bind(this), 500);
        this.input.addEventListener('input', () => {
          this.updatePreview();
          // Cart page summary: "Your note travels with the order."
          document
            .querySelectorAll('[data-note-travels]')
            .forEach((el) => (el.hidden = !this.input.value.trim()));
          if (this.status) this.status.textContent = '';
          this.save();
        });
      }

      updatePreview() {
        if (!this.preview) return;
        const value = this.input.value;
        this.preview.textContent = value.trim() ? value : 'Your words will appear here.';
        this.preview.classList.toggle('is-empty', !value.trim());
      }

      save() {
        const body = JSON.stringify({ attributes: { 'Gift note': this.input.value } });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
          .then((response) => response.json())
          .then((cart) => {
            if (!cart || cart.errors || cart.status) throw new Error('Cart update failed');
            if (this.status) this.status.textContent = 'Your note is saved with your bag.';
          })
          .catch(() => {
            // Checkout still submits the textarea with the cart form.
            if (this.status) this.status.textContent = '';
          });
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * Cart drawer extras (snippets/abbaco-cart-drawer.liquid). Dawn's
 * cart-drawer.js owns open/close, focus trap, Escape, scrim click, and
 * scroll lock; the drawer re-renders after changes, so these are delegated.
 * - Focus moves to Close when the drawer opens (design).
 * - "Check out" reads "Going to checkout" with aria-busy while navigating.
 * ------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const checkout = event.target.closest('[data-checkout-button]');
  if (!checkout || checkout.disabled) return;
  checkout.textContent = 'Going to checkout';
  checkout.setAttribute('aria-busy', 'true');
});

window.addEventListener('pageshow', () => {
  document.querySelectorAll('[data-checkout-button][aria-busy="true"]').forEach((button) => {
    button.textContent = 'Check out';
    button.setAttribute('aria-busy', 'false');
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.querySelector('cart-drawer.ab-drawer');
  if (!drawer) return;
  let wasActive = drawer.classList.contains('active');
  new MutationObserver(() => {
    const isActive = drawer.classList.contains('active');
    // Only on open, after Dawn's 240ms transition and its own focus trap.
    if (isActive && !wasActive) setTimeout(() => drawer.querySelector('.ab-drawer__close')?.focus(), 300);
    wasActive = isActive;
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
});

/* ---------------------------------------------------------------------------
 * Cart page (sections/abbaco-main-cart.liquid). Dawn's cart.js re-renders
 * #main-cart-items and #main-cart-footer with the same '.js-contents'
 * selector, which assumes two sections. Here both live in one section, so
 * the summary uses '.js-summary' and this instance tells cart.js which
 * wrapper each region should take from the re-rendered section.
 * ------------------------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  const cartItems = document.querySelector('cart-items.ab-cart');
  if (!cartItems) return;
  const sectionId = cartItems.dataset.sectionId;
  cartItems.getSectionsToRender = () => [
    { id: 'main-cart-items', section: sectionId, selector: '.js-contents' },
    { id: 'main-cart-footer', section: sectionId, selector: '.js-summary' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
  ];
});

/* ---------------------------------------------------------------------------
 * <abba-collection>: sections/abbaco-main-collection.liquid
 * Filter drawer from the left (focus trap, Escape, scrim, focus return,
 * scroll lock, accordion groups). Checking an option, removing a chip, or
 * Clear all re-renders the results, chips, count, option counts, and the
 * "Show n caps" label through the Section Rendering API, and updates the
 * URL. "Show n caps" closes the drawer.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-collection')) {
  customElements.define(
    'abba-collection',
    class AbbaCollection extends HTMLElement {
      connectedCallback() {
        this.wrap = this.querySelector('#AbFilterWrap');
        this.panel = this.querySelector('#AbFilterDrawer');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-filter-open]')) return this.openDrawer(event.target.closest('[data-filter-open]'));
          if (event.target.closest('[data-filter-close]')) return this.closeDrawer();
          const toggle = event.target.closest('[data-filter-toggle]');
          if (toggle) {
            const open = toggle.getAttribute('aria-expanded') !== 'true';
            toggle.setAttribute('aria-expanded', String(open));
            const panel = this.querySelector(`#${toggle.getAttribute('aria-controls')}`);
            if (panel) panel.hidden = !open;
            return;
          }
          const link = event.target.closest('[data-filter-link]');
          if (link) {
            event.preventDefault();
            this.update(new URL(link.href, window.location.origin));
          }
        });

        this.addEventListener('change', (event) => {
          if (!event.target.closest('[data-filter-form]')) return;
          this.update(this.formUrl());
        });

        this.addEventListener('submit', (event) => {
          if (!event.target.closest('[data-filter-form]')) return;
          event.preventDefault();
          this.closeDrawer();
        });

        this.panel?.addEventListener('keydown', (event) => this.trapFocus(event));
      }

      formUrl() {
        const form = this.querySelector('[data-filter-form]');
        const url = new URL(this.dataset.url, window.location.origin);
        new FormData(form).forEach((value, key) => url.searchParams.append(key, value));
        return url;
      }

      update(url) {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
        this.controller?.abort();
        this.controller = new AbortController();
        this.setAttribute('aria-busy', 'true');
        fetch(fetchUrl, { signal: this.controller.signal })
          .then((response) => response.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            this.swap(doc, '[data-collection-results]');
            this.swapGroups(doc);
            const show = doc.querySelector('[data-filter-show]');
            const current = this.querySelector('[data-filter-show]');
            if (show && current) current.textContent = show.textContent;
            const cleanUrl = new URL(url);
            cleanUrl.searchParams.delete('section_id');
            window.history.replaceState({}, '', cleanUrl.toString());
          })
          .catch((error) => {
            if (error.name !== 'AbortError') window.location.href = url.toString();
          })
          .finally(() => this.removeAttribute('aria-busy'));
      }

      swap(doc, selector) {
        const next = doc.querySelector(selector);
        const current = this.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      }

      // Keep which groups are open and which checkbox had focus.
      swapGroups(doc) {
        const current = this.querySelector('[data-filter-groups]');
        const next = doc.querySelector('[data-filter-groups]');
        if (!current || !next) return;
        const open = [...current.querySelectorAll('[data-filter-toggle]')].map((t) => t.getAttribute('aria-expanded'));
        const focused = document.activeElement?.closest('[data-filter-groups]') ? document.activeElement : null;
        const focusKey = focused ? `${focused.name}=${focused.value}` : null;
        current.innerHTML = next.innerHTML;
        current.querySelectorAll('[data-filter-toggle]').forEach((toggle, i) => {
          if (open[i] === undefined) return;
          toggle.setAttribute('aria-expanded', open[i]);
          const panel = current.querySelector(`#${toggle.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = open[i] !== 'true';
        });
        if (focusKey) {
          [...current.querySelectorAll('input')].find((i) => `${i.name}=${i.value}` === focusKey)?.focus();
        }
      }

      openDrawer(trigger) {
        if (!this.wrap) return;
        this.lastFocus = trigger || document.activeElement;
        this.wrap.hidden = false;
        this.querySelectorAll('[data-filter-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
        document.body.style.overflow = 'hidden';
        void this.wrap.offsetHeight;
        this.wrap.classList.add('is-shown');
        this.panel.querySelector('[data-filter-close]')?.focus();
      }

      closeDrawer() {
        if (!this.wrap || this.wrap.hidden) return;
        this.wrap.classList.remove('is-shown');
        this.querySelectorAll('[data-filter-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        document.body.style.overflow = '';
        const finish = () => {
          this.wrap.hidden = true;
          (this.querySelector('[data-filter-open]') || this.lastFocus)?.focus();
        };
        if (this.reduceMotion.matches) finish();
        else setTimeout(finish, 240);
      }

      trapFocus(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [
          ...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"])'),
        ].filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * Search page (sections/abbaco-main-search.liquid): the clear button shows
 * while the field has text and clears it, returning focus to the field.
 * ------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const clear = event.target.closest('[data-search-clear]');
  if (!clear) return;
  const input = clear.closest('form')?.querySelector('[data-search-page-input]');
  if (!input) return;
  input.value = '';
  clear.hidden = true;
  input.focus();
});

document.addEventListener('input', (event) => {
  if (!event.target.matches('[data-search-page-input]')) return;
  const clear = event.target.closest('form')?.querySelector('[data-search-clear]');
  if (clear) clear.hidden = !event.target.value;
});

/* ---------------------------------------------------------------------------
 * <abba-gift-card>: sections/abbaco-gift-card.liquid
 * "Send it to someone by email" reveals and enables Shopify's native gift
 * card recipient fields (disabled fields are not submitted), fills the
 * timezone offset Shopify expects, and counts the 200-character message.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-gift-card')) {
  customElements.define(
    'abba-gift-card',
    class AbbaGiftCard extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-recipient-toggle]');
        this.fields = this.querySelector('[data-recipient-fields]');
        this.offset = this.querySelector('[data-recipient-offset]');
        this.message = this.querySelector('[data-recipient-message]');
        this.count = this.querySelector('[data-recipient-count]');
        if (!this.toggle || !this.fields) return;
        this.toggle.setAttribute('aria-controls', this.fields.id);
        this.toggle.addEventListener('change', () => this.sync());
        this.message?.addEventListener('input', () => this.updateCount());
        this.sync();
      }

      sync() {
        const on = this.toggle.checked;
        this.toggle.setAttribute('aria-expanded', String(on));
        this.fields.hidden = !on;
        this.fields.querySelectorAll('input, textarea').forEach((el) => (el.disabled = !on));
        if (this.offset) {
          this.offset.disabled = !on;
          this.offset.value = String(new Date().getTimezoneOffset());
        }
        if (on) this.fields.querySelector('input')?.focus();
      }

      updateCount() {
        if (this.count) this.count.textContent = `${this.message.value.length} / 200`;
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-announcement>: sections/abbaco-announcement.liquid
 * The bar scrolls away with the page; the header follows it up and stays
 * fixed once the bar is gone. --ab-bar-shift = min(scrollY, 40).
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-announcement')) {
  customElements.define(
    'abba-announcement',
    class AbbaAnnouncement extends HTMLElement {
      connectedCallback() {
        this.onScroll = () => {
          const shift = Math.min(Math.max(window.scrollY, 0), 40);
          document.documentElement.style.setProperty('--ab-bar-shift', `${shift}px`);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        document.documentElement.style.removeProperty('--ab-bar-shift');
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-reviews>: sections/abbaco-reviews.liquid
 * Previous / next scroll the row by one card (card width + 24px gap).
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-reviews')) {
  customElements.define(
    'abba-reviews',
    class AbbaReviews extends HTMLElement {
      connectedCallback() {
        const list = this.querySelector('[data-reviews-list]');
        if (!list) return;
        this.querySelectorAll('[data-dir]').forEach((button) => {
          button.addEventListener('click', () => {
            const card = list.querySelector('li');
            const step = (card ? card.offsetWidth : 440) + 24;
            list.scrollBy({ left: Number(button.dataset.dir) * step });
          });
        });
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-note-preview>: sections/abbaco-note-preview.liquid
 * Mirrors the practice textarea into the card; empty shows the Muted hint.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-note-preview')) {
  customElements.define(
    'abba-note-preview',
    class AbbaNotePreview extends HTMLElement {
      connectedCallback() {
        const input = this.querySelector('[data-note-input]');
        const preview = this.querySelector('[data-note-preview]');
        if (!input || !preview) return;
        const sync = () => {
          const text = input.value;
          const empty = !text.trim();
          preview.textContent = empty ? 'Your words will appear here.' : text;
          preview.classList.toggle('is-empty', empty);
        };
        input.addEventListener('input', sync);
        sync();
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-article>: sections/abbaco-main-article.liquid
 * Reading progress (3px Blaze bar) across the article, and "Copy link".
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-article')) {
  customElements.define(
    'abba-article',
    class AbbaArticle extends HTMLElement {
      connectedCallback() {
        const bar = this.querySelector('[data-progress]');
        const body = this.querySelector('[data-article-body]');
        if (bar && body) {
          const update = () => {
            const r = body.getBoundingClientRect();
            const total = r.height - window.innerHeight;
            const p = total > 0 ? Math.min(100, Math.max(0, (-r.top / total) * 100)) : 100;
            bar.style.width = `${p}%`;
          };
          window.addEventListener('scroll', update, { passive: true });
          window.addEventListener('resize', update);
          update();
        }
        const copy = this.querySelector('[data-copy-link]');
        const status = this.querySelector('[data-copy-status]');
        copy?.addEventListener('click', () => {
          const done = () => {
            copy.textContent = 'Link copied';
            if (status) status.textContent = 'Link copied';
            setTimeout(() => (copy.textContent = 'Copy link'), 2000);
          };
          if (navigator.clipboard) navigator.clipboard.writeText(window.location.href).then(done, done);
          else done();
        });
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-info>: sections/abbaco-info-page.liquid
 * "On this page" marks the section in view (aria-current="location", Blaze
 * bar) and scrolls smoothly to a section, 140px below the header.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-info')) {
  customElements.define(
    'abba-info',
    class AbbaInfo extends HTMLElement {
      connectedCallback() {
        const links = [...this.querySelectorAll('[data-toc-link]')];
        const sections = [...this.querySelectorAll('[data-info-sec]')];
        if (!links.length || !sections.length) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const mark = () => {
          let active = 0;
          sections.forEach((sec, i) => {
            if (sec.getBoundingClientRect().top < 180) active = i;
          });
          links.forEach((link, i) => {
            if (i === active) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
          });
        };
        links.forEach((link, i) =>
          link.addEventListener('click', (event) => {
            const target = sections[i];
            if (!target) return;
            event.preventDefault();
            window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 140, behavior: reduce ? 'auto' : 'smooth' });
            history.replaceState(null, '', `#${target.id}`);
          })
        );
        window.addEventListener('scroll', mark, { passive: true });
        mark();
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-dialog>: a modal opened by any [data-dialog-open][aria-controls=id]
 * (password page). Focus moves to the first field, Tab is trapped, Escape
 * or a backdrop click closes it, focus returns to the opener, and the page
 * behind does not scroll.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-dialog')) {
  customElements.define(
    'abba-dialog',
    class AbbaDialog extends HTMLElement {
      connectedCallback() {
        document.querySelectorAll(`[data-dialog-open][aria-controls="${this.id}"]`).forEach((button) =>
          button.addEventListener('click', () => this.open(button))
        );
        this.querySelectorAll('[data-dialog-close]').forEach((button) => button.addEventListener('click', () => this.close()));
        this.addEventListener('click', (event) => {
          if (event.target === this) this.close();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
            return;
          }
          if (event.key !== 'Tab') return;
          const items = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, select')].filter(
            (el) => el.offsetParent !== null
          );
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        });
      }

      open(opener) {
        this.opener = opener;
        this.hidden = false;
        opener?.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        this.querySelector('input, button')?.focus();
      }

      close() {
        this.hidden = true;
        this.opener?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
        this.opener?.focus();
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-shop-caps>: sections/abbaco-shop-caps.liquid (Homepage v3)
 * - Tabs All / Ready to ship / Preorder filter the cards on the page by
 *   data-kind. Arrow keys, Home and End move between tabs (automatic
 *   activation, roving tabindex).
 * - Any [data-shop-tab] link on the page (the hero's "Preorder the next cap")
 *   selects that tab; the link's #shop href does the scrolling.
 * - When Dawn's product form adds a card's cap, its button reads "Added" for
 *   1.8s (the bag drawer opens on its own).
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-shop-caps')) {
  customElements.define(
    'abba-shop-caps',
    class AbbaShopCaps extends HTMLElement {
      connectedCallback() {
        this.tablist = this.querySelector('[role="tablist"]');
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.panel = this.querySelector('[role="tabpanel"]');
        this.items = [...this.querySelectorAll('[data-kind]')];
        if (this.tablist && this.tabs.length) {
          this.tablist.hidden = false;
          this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab.dataset.tab)));
          this.tablist.addEventListener('keydown', (event) => this.onKeyDown(event));
        }

        this.onLinkClick = (event) => {
          const link = event.target.closest('[data-shop-tab]');
          if (link) this.select(link.dataset.shopTab);
        };
        document.addEventListener('click', this.onLinkClick);

        const hook = () => {
          if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onAdded(event));
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', hook, { once: true });
        } else {
          hook();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onLinkClick);
        this.unsubscribe?.();
      }

      select(key, focus = false) {
        const tab = this.tabs.find((t) => t.dataset.tab === key);
        if (!tab) return;
        this.tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        this.panel?.setAttribute('aria-labelledby', tab.id);
        this.items.forEach((item) => {
          item.hidden = key !== 'all' && item.dataset.kind !== key;
        });
        if (focus) tab.focus();
      }

      onKeyDown(event) {
        const current = this.tabs.indexOf(document.activeElement);
        if (current < 0) return;
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + this.tabs.length) % this.tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.tabs.length - 1;
        else return;
        event.preventDefault();
        this.select(this.tabs[next].dataset.tab, true);
      }

      onAdded(event) {
        if (event.source !== 'product-form' || !event.productVariantId) return;
        const input = this.querySelector(`input[name="id"][value="${event.productVariantId}"]`);
        const button = input?.form?.querySelector('[data-label]');
        const label = button?.querySelector('span');
        if (!label) return;
        label.textContent = 'Added';
        button.classList.add('is-added');
        clearTimeout(button.addedTimer);
        button.addedTimer = setTimeout(() => {
          label.textContent = button.dataset.label;
          button.classList.remove('is-added');
        }, 1800);
      }
    }
  );
}

/* ---------------------------------------------------------------------------
 * <abba-hero-carousel>: sections/abbaco-hero-split.liquid, rotating mode
 * - Crossfade (CSS, 500ms) to the next slide every 6s.
 * - Pauses while hovered or focused; previous, next or a dot stops it.
 * - Never autoplays under prefers-reduced-motion or in the theme editor,
 *   unless Play is pressed.
 * - Hidden slides get aria-hidden and tabindex=-1 on their links.
 * ------------------------------------------------------------------------- */
if (!customElements.get('abba-hero-carousel')) {
  customElements.define(
    'abba-hero-carousel',
    class AbbaHeroCarousel extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-slide]')];
        if (this.slides.length < 2) return;
        this.dots = [...this.querySelectorAll('[data-dot]')];
        this.playButton = this.querySelector('[data-play]');
        this.index = 0;
        this.hovered = false;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.playing = !reduce && !window.Shopify?.designMode;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.dot), true)));
        this.playButton?.addEventListener('click', () => this.setPlaying(!this.playing));
        this.addEventListener('mouseenter', () => (this.hovered = true));
        this.addEventListener('mouseleave', () => (this.hovered = false));
        this.addEventListener('focusin', () => (this.hovered = true));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.hovered = false;
        });
        this.addEventListener('shopify:block:select', (event) => {
          const i = this.slides.indexOf(event.target);
          if (i >= 0) this.go(i, true);
        });

        this.timer = setInterval(() => {
          if (this.playing && !this.hovered) this.go(this.index + 1);
        }, 6000);
        this.setPlaying(this.playing);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      go(target, stop = false) {
        const count = this.slides.length;
        this.index = (target + count) % count;
        this.slides.forEach((slide, i) => {
          const on = i === this.index;
          slide.classList.toggle('is-active', on);
          if (on) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
          slide.querySelectorAll('a, button').forEach((el) => {
            if (on) el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', '-1');
          });
        });
        this.dots.forEach((dot, i) => {
          if (i === this.index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        if (stop) this.setPlaying(false);
      }

      setPlaying(playing) {
        this.playing = playing;
        if (!this.playButton) return;
        this.playButton.textContent = playing ? 'Pause' : 'Play';
        this.playButton.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
      }
    }
  );
}
